import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/hooks/useAuthStore';
import {
  LayoutDashboard,
  Building2,
  Truck,
  ClipboardList,
  FileSearch,
  Users,
  UserCheck,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ArrowLeft,
  Shield,
  Activity,
  CreditCard,
  DollarSign,
  MapPin,
  Kanban,
  ShoppingCart,
  Brain,
  Landmark,
} from 'lucide-react';
import AIAssistant from '../AIAssistant';


const navigation = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Internal Operations', href: '/admin/internal', icon: Activity },
  { name: 'Customer Requests', href: '/admin/customer-requests', icon: Kanban },
  { name: 'Purchase Orders', href: '/admin/purchase-orders', icon: ShoppingCart },
  { name: 'Internal Tools', href: '/admin/tools', icon: Brain, children: [
    { name: 'Waste Intelligence', href: '/admin/intel' },
    { name: 'Lead Generator', href: '/admin/leads' },
    { name: 'Vendor Discovery', href: '/admin/vendors' },
    { name: 'Lab Analyzer', href: '/admin/lab-analyzer' },
    { name: 'Lab Pack Planner', href: '/admin/lab-packs' },
    { name: 'Manifest Printer', href: '/admin/manifest-printer' },
    { name: 'E-Signatures', href: '/admin/signatures' },
  ]},
  { name: 'Govt Contracts', href: '/admin/sam-gov', icon: Landmark, children: [
    { name: 'Opportunities', href: '/admin/sam-gov' },
    { name: 'Bid Pipeline', href: '/admin/sam-gov/pipeline' },
    { name: 'Scoring Keywords', href: '/admin/sam-gov/keywords' },
  ]},
  { name: 'Partners', href: '/admin/partners', icon: Building2, children: [
    { name: 'TSDF Facilities', href: '/admin/partners/tsdf' },
    { name: 'Transporters', href: '/admin/partners/transporters' },
  ]},
  { name: 'Transactions', href: '/admin/transactions', icon: ClipboardList },
  { name: 'Profile Review', href: '/admin/profiles', icon: FileSearch },
  { name: 'Route Planning', href: '/admin/route-planning', icon: MapPin },
  { name: 'Pricing Confirmation', href: '/admin/pricing', icon: DollarSign },
  { name: 'Rate Estimates', href: '/admin/rate-estimates', icon: DollarSign },
  { name: 'Quote Builder', href: '/admin/quote-builder', icon: ClipboardList },
  { name: 'Credit Management', href: '/admin/credit', icon: CreditCard },
  { name: 'Customers', href: '/admin/customers', icon: Users },
  { name: 'Authorized Agents', href: '/admin/authorized-agents', icon: UserCheck },
  { name: 'Reports', href: '/admin/reports', icon: BarChart3 },
  { name: 'Configuration', href: '/admin/config', icon: Settings },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isNavActive = (item: typeof navigation[0]) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.href);
    }
    return location.pathname === item.href;
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
      >
        <div
          className="fixed inset-0 bg-black/60"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 w-64 bg-gray-800 shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-orange-500" />
              <span className="text-xl font-bold text-white">Admin</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-gray-700 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = isNavActive(item);
              const isExpanded = expandedNav === item.name;

              if (item.children) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedNav(isExpanded ? null : item.name)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${
                        isActive
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="mt-1 ml-8 space-y-1">
                        {item.children.map(child => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`block px-3 py-2 rounded-md text-sm ${
                              location.pathname === child.href
                                ? 'bg-orange-600 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    isActive
                      ? 'bg-orange-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-gray-800 border-r border-gray-700">
          <div className="flex items-center h-16 px-6 border-b border-gray-700">
            <Shield className="w-6 h-6 text-orange-500 mr-2" />
            <span className="text-xl font-bold text-white">Admin Portal</span>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = isNavActive(item);
              const isExpanded = expandedNav === item.name || isActive;

              if (item.children) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedNav(isExpanded && expandedNav === item.name ? null : item.name)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${
                        isActive
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="mt-1 ml-8 space-y-1">
                        {item.children.map(child => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block px-3 py-2 rounded-md text-sm ${
                              location.pathname === child.href
                                ? 'bg-orange-600 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    isActive
                      ? 'bg-orange-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-700">
            <Link
              to="/"
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-400 rounded-md hover:bg-gray-700 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-3" />
              Back to App
            </Link>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-40 flex items-center h-16 px-4 bg-gray-800 border-b border-gray-700 sm:px-6 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md lg:hidden hover:bg-gray-700 text-gray-400"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-gray-700"
            >
              <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center">
                <span className="text-white font-medium">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <div className="font-medium text-white">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-gray-400">Administrator</div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg border border-gray-700 py-1">
                  <div className="px-4 py-2 border-b border-gray-700">
                    <div className="text-sm font-medium text-white">
                      {user?.email}
                    </div>
                    <div className="text-xs text-gray-400">
                      Admin Role
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 bg-gray-900 min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>

      {/* AI Regulatory Assistant */}
      <AIAssistant />
    </div>
  );
}
